import { CSSProperties, useEffect, useState } from 'react';
import { colors, hexToRgbA } from '../../theme/colors';
import CircleLoadingIndicator from './CircleLoadingIndicator';
import { MainButton } from '../Buttons/MainButton';
import { t } from 'i18next';
import spacing from '../../theme/spacing';

const useReloadButton = ({
  showReloadAfterMs,
}: {
  showReloadAfterMs?: number;
}) => {
  const [showReloadButton, setShowReloadButton] = useState(false);

  useEffect(() => {
    if (showReloadAfterMs) {
      const timer = setTimeout(() => {
        setShowReloadButton(true);
      }, showReloadAfterMs);

      return () => clearTimeout(timer);
    }
  }, [showReloadAfterMs]);

  const handleReload = () => {
    window.location.reload();
  };

  return { showReloadButton, handleReload };
};

const LoadingScreen = ({
  absolute,
  containerStyle,
  indicatorSize = 'large',
  showReloadAfterMs,
}: {
  absolute?: boolean;
  containerStyle?: CSSProperties;
  indicatorSize?: number | 'small' | 'medium' | 'large';
  showReloadAfterMs?: number;
}) => {
  const { showReloadButton, handleReload } = useReloadButton({
    showReloadAfterMs,
  });

  const getSizeFromIndicator = (
    indicatorSize: number | 'small' | 'medium' | 'large',
  ) => {
    const sizeMapping = {
      small: 24,
      medium: 30,
      large: 48,
    };
    return typeof indicatorSize === 'number'
      ? indicatorSize
      : sizeMapping[indicatorSize];
  };

  const size = getSizeFromIndicator(indicatorSize);

  return (
    <div
      style={{
        minHeight: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: spacing.large,
        ...(absolute
          ? {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              textAlign: 'center',
              background: hexToRgbA(colors.backgroundMediumLight, 0.25),
            }
          : {}),
        ...containerStyle,
      }}
    >
      <CircleLoadingIndicator size={size} />
      {showReloadButton && (
        <MainButton
          onClick={handleReload}
          typeStyle="secondary"
          variant="outlined"
          text={t('common.reload')}
        />
      )}
    </div>
  );
};

export default LoadingScreen;
